import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config/api';

const inputStyle = {
  width: '100%',
  padding: '12px',
  backgroundColor: '#1a1a1a',
  border: '1px solid #3a3a3a',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '1rem',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  color: '#d1d5db',
  fontWeight: '600'
};

export default function TokenRegistration() {
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [xrplIssuer, setXrplIssuer] = useState('');
  const [decimals] = useState(0); // Most XRPL tokens use 0 decimals
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [registeredTokens, setRegisteredTokens] = useState([]);

  // XUMM payment flow
  const [xamanData, setXamanData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    loadApprovedTokens();

    // Check for recent successful registration
    const recentRegistration = sessionStorage.getItem('recent_token_registration');
    if (recentRegistration) {
      try {
        const regData = JSON.parse(recentRegistration);
        const timeSinceReg = Date.now() - regData.timestamp;

        // Show success message if registration was within last 10 minutes
        if (timeSinceReg < 10 * 60 * 1000) {
          setMessage(`✅ Success! Your token "${regData.symbol}" has been registered and is pending admin approval. You will be notified once the wrapped token is deployed on Flare.`);
          setMessageType('success');
        } else {
          // Clear old registration data
          sessionStorage.removeItem('recent_token_registration');
        }
      } catch (e) {
        console.error('Error parsing registration data:', e);
      }
    }
  }, []);

  // Poll XUMM payload status when xamanData is set
  useEffect(() => {
    if (!xamanData || paymentComplete) return;

    let pollInterval;

    const pollPaymentStatus = async () => {
      // Check if another tab/window is already completing this registration
      // This prevents race conditions when user has multiple tabs open
      const completionKey = `completing_${xamanData.uuid}`;
      const isCompletingGlobal = localStorage.getItem(completionKey);

      if (isCompletingGlobal === 'true') {
        console.log('[REGISTRATION] Another tab is completing this registration, skipping...');
        return;
      }

      try {
        const res = await fetch(`${BACKEND_URL}/api/tokens/register/status/${xamanData.uuid}`);
        const data = await res.json();

        if (data.expired) {
          setPaymentStatus('Payment request expired. Please try again.');
          clearInterval(pollInterval);
          return;
        }

        if (data.signed && data.txid) {
          // Set global completion flag immediately to prevent other tabs from racing
          localStorage.setItem(completionKey, 'true');

          // Payment complete! Now submit the registration
          setPaymentStatus('Payment confirmed! Submitting registration...');
          setPaymentComplete(true);
          clearInterval(pollInterval);

          try {
            // Complete the registration with the txid
            await completeRegistration(data.txid);
          } finally {
            // Clean up the completion flag after 30 seconds
            setTimeout(() => {
              localStorage.removeItem(completionKey);
            }, 30000);
          }
        } else if (data.signed) {
          setPaymentStatus('Payment signed, waiting for XRPL confirmation...');
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
        // Clear completion flag on error so user can retry
        localStorage.removeItem(completionKey);
      }
    };

    // Poll every 3 seconds
    pollPaymentStatus(); // Initial check
    pollInterval = setInterval(pollPaymentStatus, 3000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [xamanData, paymentComplete]);

  const loadApprovedTokens = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tokens/list?status=approved`);
      const data = await response.json();
      setRegisteredTokens(data.tokens || []);
    } catch (error) {
      console.error('Error loading tokens:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage('Image too large. Maximum size is 10MB.');
      setMessageType('error');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setMessage('Please upload an image file.');
      setMessageType('error');
      return;
    }

    setUploadingImage(true);
    setMessage('');

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;

        // Show preview
        setImagePreview(base64Data);

        // Upload to Pinata via backend
        const response = await fetch(`${BACKEND_URL}/api/tokens/upload-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_data: base64Data,
            filename: file.name
          })
        });

        const data = await response.json();

        if (response.ok && data.ipfs_url) {
          setImageUrl(data.ipfs_url);
          setMessage('Image uploaded successfully!');
          setMessageType('success');
        } else {
          setMessage(data.detail || 'Failed to upload image. Please try again.');
          setMessageType('error');
          setImagePreview('');
        }

        setUploadingImage(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      setMessage('Failed to upload image. Please try again.');
      setMessageType('error');
      setUploadingImage(false);
      setImagePreview('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!xrplIssuer.startsWith('r') || xrplIssuer.length < 25) {
      setMessage('Invalid XRPL issuer address — should start with "r"');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');
    setPaymentStatus('');
    setPaymentComplete(false);

    try {
      // Step 1: Initialize registration and create XUMM payment request
      const response = await fetch(`${BACKEND_URL}/api/tokens/register/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: symbol.toUpperCase().trim(),
          name: name.trim(),
          xrpl_issuer: xrplIssuer.trim(),
          decimals: decimals
        })
      });

      const data = await response.json();

      if (response.ok) {
        setXamanData(data);
        setPaymentStatus('Waiting for payment in Xaman...');
      } else {
        setMessage(data.detail || 'Failed to create payment request. Please try again.');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Could not connect to server. Please try again.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async (txid) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tokens/register/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol: symbol.toUpperCase().trim(),
          name: name.trim(),
          xrpl_issuer: xrplIssuer.trim(),
          decimals: decimals,
          payment_txid: txid,
          image_url: imageUrl
        })
      });

      const data = await response.json();

      if (response.ok) {
        const registeredSymbol = symbol.toUpperCase();

        // Store success in sessionStorage so it persists across page navigation
        sessionStorage.setItem('recent_token_registration', JSON.stringify({
          symbol: registeredSymbol,
          timestamp: Date.now()
        }));

        setMessage(`✅ Success! Your token "${registeredSymbol}" has been registered and is pending admin approval. You will be notified once the wrapped token is deployed on Flare.`);
        setMessageType('success');
        setSymbol('');
        setName('');
        setXrplIssuer('');
        setImageUrl('');
        setImagePreview('');
        setXamanData(null);
        setPaymentStatus('');
        setPaymentComplete(false);
        await loadApprovedTokens();
      } else {
        setMessage(data.detail || 'Registration failed. Please try again.');
        setMessageType('error');
        setPaymentStatus('');
      }
    } catch (error) {
      setMessage('Could not complete registration. Please try again.');
      setMessageType('error');
      setPaymentStatus('');
    }
  };

  const cancelPayment = () => {
    setXamanData(null);
    setPaymentStatus('');
    setPaymentComplete(false);
    setMessage('');
  };

  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      <h2 style={{ color: '#A78BFA', marginBottom: '8px' }}>List Your Token</h2>
      <p style={{ color: '#9ca3af', marginBottom: '24px', lineHeight: '1.6' }}>
        Unlock cross-chain DeFi for your XRPL token holders. Enable swapping, liquidity earning, and yield farming on Flare Network.
        {' '}One-time fee: <strong style={{ color: '#A78BFA' }}>50 XRP</strong>
      </p>

      {/* Registration Form */}
      {!xamanData && (
        <div style={{
          backgroundColor: '#2a2a2a',
          border: '1px solid #3a3a3a',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ color: '#A78BFA', fontWeight: '700', marginBottom: '20px' }}>
            Token Details
          </div>

          {message && (
            <div style={{
              padding: '14px',
              backgroundColor: messageType === 'success' ? '#064e3b' : '#7f1d1d',
              border: `1px solid ${messageType === 'success' ? '#10b981' : '#ef4444'}`,
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#fff',
              lineHeight: '1.6'
            }}>
              {messageType === 'success' ? '✅ ' : '❌ '}{message}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={labelStyle}>
                Token Ticker <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. FUZZY"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>
                Token Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. FUZZY BEAR"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>
                XRPL Issuer Address <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="r..."
                value={xrplIssuer}
                onChange={(e) => setXrplIssuer(e.target.value)}
                required
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              <small style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                The XRPL account address that issued your token
              </small>
            </div>

            <div>
              <label style={labelStyle}>
                Token Image (Optional)
              </label>
              <div style={{
                border: '2px dashed #3a3a3a',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#1a1a1a',
                cursor: 'pointer'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ display: 'none' }}
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  style={{
                    cursor: uploadingImage ? 'not-allowed' : 'pointer',
                    display: 'block'
                  }}
                >
                  {imagePreview ? (
                    <div>
                      <img
                        src={imagePreview}
                        alt="Token preview"
                        style={{
                          maxWidth: '120px',
                          maxHeight: '120px',
                          borderRadius: '8px',
                          marginBottom: '8px'
                        }}
                      />
                      <div style={{ color: '#10b981', fontSize: '0.875rem' }}>
                        ✓ Image uploaded to IPFS
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📷</div>
                      <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                        {uploadingImage ? 'Uploading to IPFS...' : 'Click to upload token logo'}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '4px' }}>
                        Square image recommended (512x512px, max 10MB)
                      </div>
                    </div>
                  )}
                </label>
              </div>
              <small style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                Your token logo will be displayed on the leaderboard
              </small>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '14px',
                backgroundColor: loading ? '#4b5563' : '#FB923C',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                marginTop: '4px'
              }}
            >
              {loading ? 'Creating Payment Request...' : 'Continue to Payment'}
            </button>
          </form>
        </div>
      )}

      {/* Xaman Payment Panel */}
      {xamanData && (
        <div style={{
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: '#1a1a2e',
          border: '1px solid #0073cc',
          borderRadius: '12px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#A78BFA', fontWeight: '700', fontSize: '1rem', marginBottom: '4px' }}>
            Pay Registration Fee in Xaman
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '16px' }}>
            {isMobile ? 'Tap the button below to open Xaman' : 'Scan with Xaman or click the button'}
          </div>

          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#1a1a1a',
            border: '1px solid #8B5CF6',
            borderRadius: '8px',
          }}>
            <div style={{ color: '#A78BFA', fontWeight: '600', fontSize: '0.9rem', marginBottom: '8px' }}>
              Amount: {xamanData.fee_xrp} XRP
            </div>
            <div style={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              To: {xamanData.fee_wallet}
            </div>
          </div>

          {/* QR code — only useful on desktop */}
          {!isMobile && (
            <img
              src={xamanData.qr_png}
              alt="Scan with Xaman"
              style={{ width: '180px', height: '180px', borderRadius: '8px', marginBottom: '16px' }}
            />
          )}

          {/* Primary: HTTPS universal link (iOS/Android app link) */}
          <div>
            <a
              href={xamanData.sign_url}
              style={{
                display: 'inline-block',
                padding: '14px 36px',
                backgroundColor: '#0073cc',
                color: '#fff',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: isMobile ? '1.1rem' : '0.95rem',
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box',
              }}
            >
              Open in Xaman
            </a>
          </div>

          {/* Secondary: URI scheme fallback (shown on mobile only) */}
          {isMobile && (
            <div style={{ marginTop: '10px' }}>
              <a
                href={`xumm://sign/${xamanData.uuid}`}
                style={{
                  display: 'inline-block',
                  padding: '10px 24px',
                  backgroundColor: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #3a3a3a',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                Try alternative link
              </a>
            </div>
          )}

          <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '12px' }}>
            This request expires in 10 minutes
          </div>

          {/* Payment status */}
          {paymentStatus && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              backgroundColor: paymentComplete ? '#14532d' : '#1e3a5f',
              border: `1px solid ${paymentComplete ? '#4ade80' : '#3b82f6'}`,
              borderRadius: '8px',
              color: paymentComplete ? '#4ade80' : '#93c5fd',
              fontSize: '0.85rem',
            }}>
              {paymentComplete ? '✓ ' : '⏳ '}{paymentStatus}
            </div>
          )}

          <button
            onClick={cancelPayment}
            style={{
              marginTop: '12px',
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Live tokens list */}
      <div>
        <h3 style={{ color: '#A78BFA', marginBottom: '14px' }}>
          Live Tokens ({registeredTokens.length})
        </h3>
        {registeredTokens.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '32px',
            backgroundColor: '#2a2a2a',
            borderRadius: '12px',
            color: '#9ca3af'
          }}>
            No tokens live yet
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            {registeredTokens.map(token => (
              <div key={token.symbol} style={{
                backgroundColor: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                padding: '14px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <span style={{ color: '#A78BFA', fontWeight: '700', fontSize: '1.05rem' }}>
                    ${token.symbol}
                  </span>
                  <span style={{ color: '#9ca3af', marginLeft: '10px', fontSize: '0.875rem' }}>
                    {token.name}
                  </span>
                </div>
                <code style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {token.xrpl_issuer.slice(0, 8)}...
                </code>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
